import {
  Accordion,
  AccordionVariant,
  Button,
  ButtonVariant,
  CheckBox,
  CheckboxAlignment,
  ColorClass,
  Divider,
  FormMessage,
  FormMessageVariant,
  Icon,
  IconIdentifier,
  Input,
  Spinner,
  Tooltip,
  toast,
} from '@aus-platform/design-system';
import { useAppSelector } from 'app/hooks';
import classNames from 'classnames';
import FileSaver from 'file-saver';
import { has, isEmpty, isNil, isUndefined, size } from 'lodash';
import {
  selectMap3DState,
  selectMap3dDataset,
} from 'map-3d/shared/map-3d-slices';
import React, { useContext, useEffect, useState } from 'react';
import type { AccordionEventKey } from 'react-bootstrap/esm/AccordionContext';
import { trackEvent } from 'shared/analytics';
import type { HeapClusterResponseDataTypeObj } from 'shared/api';
import {
  BaseReference,
  ClusterStatus,
  VolumeReportFormat,
  handleResponseErrorMessage,
  handleResponseMessage,
  useDeleteHeap,
  useDeleteHeapClusterFromDashboard,
  useDownloadHeap,
  useDownloadVolumeReport,
  useRecalculateHeapVolume,
} from 'shared/api';
import { ConfirmationModal } from 'shared/components';
import { GlobalContext } from 'shared/context';
import { EventPropertyKey, Events, FeatureFlag } from 'shared/enums';
import { Formatter } from 'shared/helpers';
import { useDashboardCategoryConfig } from 'shared/hooks';
import { baseReferenceLabel, getMaterialTypeLabel } from '../../constants';
import { DashboardModal } from '../dashboard/dashboard-modal';
import { EditHeap } from '../edit-heap';
import { flyToCentroid } from '../helpers';
import { HeapBulkSelectCheckBoxStatus } from './enums';
import { HeapClusterCalculationStatus } from './heap-cluster-calculation-status';
import type {
  HeapDeleteConfirmationType,
  HeapListPropsType,
  ShiftKeyRangeSelectedHeapType,
} from './types';

const deleteHeapInitialState: HeapDeleteConfirmationType = {
  show: false,
  cluster: null,
};

const shiftKeyRangeSelectedHeapInitialState = {
  startingIndex: -1,
  endingIndex: -1,
  isStartHeapSelected: false,
};

export const HeapList: React.FC<HeapListPropsType> = ({
  heapListResponse,
  isHeapListLoading,
  heapListRefetch,
  selectedClusters,
  onSelectClusterHandler,
  setIsHeapsTableVisible,
  isHeapsTableVisible,
  setActiveHeapCluster,
  activeHeapCluster,
  setIsHeapClusterModified,
  importWKTPolygon,
  scrollToClusterId,
  setScrollToClusterId,
}) => {
  // States.
  const [showDeleteConfirmationCard, setShowDeleteConfirmationCard] =
    useState<HeapDeleteConfirmationType>(deleteHeapInitialState);
  const [showEditHeapModal, setShowEditHeapModal] = useState(false);
  const [heapId, setHeapId] = useState('');
  const [activeKey, setActiveKey] = useState<AccordionEventKey>('');
  const [volumeReportFormat, setVolumeReportFormat] =
    useState<VolumeReportFormat>();
  const [bulkSelectCheckBoxStatus, setBulkSelectCheckBoxStatus] =
    useState<HeapBulkSelectCheckBoxStatus>(
      HeapBulkSelectCheckBoxStatus.Unchecked,
    );
  const [shiftKeyRangeSelectedHeap, setShiftKeyRangeSelectedHeap] =
    useState<ShiftKeyRangeSelectedHeapType>(
      shiftKeyRangeSelectedHeapInitialState,
    );
  const [isDashboardModalVisible, setIsDashboardModalVisible] = useState(false);
  const [selectedDashboardCluster, setSelectedDashboardCluster] =
    useState<HeapClusterResponseDataTypeObj | null>(null);
  const [
    isRemoveDashboardRecordModalVisible,
    setIsRemoveDashboardRecordModalVisible,
  ] = useState(false);

  // Selectors.
  const { cesiumProxy, aoiManager } = useAppSelector(selectMap3DState);
  const { selectedTerrainIteration } = useAppSelector(selectMap3dDataset);

  // Drawing tools.
  const polygonTool = aoiManager?.polygonTool;

  // Hooks.
  const {
    dashboardCategoryConfig,
    isLoading: isDashboardConfigLoading,
    refetchDashboardConfig,
  } = useDashboardCategoryConfig();

  // Context.
  const { loggedUser } = useContext(GlobalContext);

  // Variables.
  const isProductivityDashboardEnabled =
    !!loggedUser?.featureFlags?.[FeatureFlag.ProductivityDashboard];

  // Apis.
  const {
    mutate: sendDownloadVolumeReportRequest,
    data: downloadVolumeReportResponse,
    isSuccess: isDownloadVolumeReportSuccess,
    isPending: isVolumeReportLoading,
    isError: isDownloadVolumeReportHasError,
    error: downloadVolumeReportError,
  } = useDownloadVolumeReport();

  const {
    mutate: sendDeleteHeapRequest,
    isSuccess: isSuccessDeleteHeap,
    isError: isErrorDeleteHeap,
    error: deleteHeapError,
  } = useDeleteHeap();

  const {
    mutate: sendRecalculateHeapVolumeRequest,
    data: recalculateHeapVolumeResponse,
    isSuccess: isSuccessRecalculateHeapVolume,
    isError: isErrorRecalculateHeapVolume,
    error: recalculateHeapVolumeError,
  } = useRecalculateHeapVolume();

  const {
    mutate: sendDownloadHeapRequest,
    data: downloadHeapResponse,
    isSuccess: isSuccessDownloadHeap,
  } = useDownloadHeap();

  const {
    mutate: deleteHeapClusterFromDashboard,
    isSuccess: isSuccessDeleteHeapClusterFromDashboard,
    isError: isErrorDeleteHeapClusterFromDashboard,
    isPending: isDeleteHeapClusterFromDashboardLoading,
  } = useDeleteHeapClusterFromDashboard();

  // useEffects.
  useEffect(() => {
    if (selectedTerrainIteration?.id) {
      heapListRefetch();
    }
  }, [polygonTool]);

  useEffect(() => {
    if (isSuccessDeleteHeap) {
      heapListRefetch();
      toast.success('Heap Cluster deleted successfully');
    }

    handleResponseErrorMessage(isErrorDeleteHeap, deleteHeapError);
  }, [isSuccessDeleteHeap, isErrorDeleteHeap, deleteHeapError]);

  useEffect(() => {
    if (isSuccessRecalculateHeapVolume && recalculateHeapVolumeResponse) {
      const heapResponse: HeapClusterResponseDataTypeObj =
        recalculateHeapVolumeResponse.data;

      // Delete existing polygon for this cluster to prevent duplicates
      if (!heapResponse.numberOfHeaps) {
        polygonTool?.deletePolygonsByProperty('heapId', heapResponse.id);
      } else {
        heapResponse.heaps.forEach((heap) => {
          polygonTool?.deletePolygonsByProperty('heapId', heap.id);
        });
      }

      // User clicks on recalculate heap without selecting it.
      if (!has(selectedClusters, heapResponse.id)) {
        onSelectClusterHandler?.(heapResponse);
      } else {
        // If already selected, reimport the polygon with updated data
        if (isEmpty(heapResponse.heaps)) {
          importWKTPolygon(
            heapResponse.name,
            heapResponse.id,
            heapResponse.geometry,
          );
        } else {
          heapResponse.heaps.forEach((heap) => {
            importWKTPolygon(heap.name, heap.id, heap.geometry);
          });
        }
      }
    }

    handleResponseMessage(
      isSuccessRecalculateHeapVolume,
      isErrorRecalculateHeapVolume,
      recalculateHeapVolumeResponse,
      recalculateHeapVolumeError,
    );
    heapListRefetch();
  }, [
    isSuccessRecalculateHeapVolume,
    isErrorRecalculateHeapVolume,
    recalculateHeapVolumeResponse,
  ]);

  useEffect(() => {
    if (downloadHeapResponse && isSuccessDownloadHeap) {
      const heap = heapListResponse?.data.find((heap) => heap.id === heapId);
      if (!isUndefined(heap)) {
        FileSaver.saveAs(downloadHeapResponse, `${heap.name}.geojson`);
      }
    }
  }, [downloadHeapResponse, isSuccessDownloadHeap]);

  useEffect(() => {
    if (
      downloadVolumeReportResponse &&
      isDownloadVolumeReportSuccess &&
      selectedTerrainIteration
    ) {
      FileSaver.saveAs(
        downloadVolumeReportResponse,
        `${selectedTerrainIteration.name}-volume-report.${volumeReportFormat}`,
      );
    }
    handleResponseErrorMessage(
      isDownloadVolumeReportHasError,
      downloadVolumeReportError,
    );
  }, [isDownloadVolumeReportSuccess, downloadVolumeReportResponse]);

  // useEffect - handle heap's bulk select checkbox status.
  useEffect(() => {
    const selectedHeapsLength = size(selectedClusters);

    // No heaps are selected - unchecked.
    if (selectedHeapsLength === 0) {
      setBulkSelectCheckBoxStatus(HeapBulkSelectCheckBoxStatus.Unchecked);
    }
    // Some heaps are selected - indeterminate.
    else if (
      selectedHeapsLength > 0 &&
      selectedHeapsLength !== heapListResponse?.data.length
    ) {
      setBulkSelectCheckBoxStatus(HeapBulkSelectCheckBoxStatus.Indeterminate);
    }
    // All heaps are selected - checked.
    else {
      setBulkSelectCheckBoxStatus(HeapBulkSelectCheckBoxStatus.Checked);
    }
  }, [selectedClusters]);

  useEffect(() => {
    if (isSuccessDeleteHeapClusterFromDashboard) {
      heapListRefetch();
      setIsRemoveDashboardRecordModalVisible(false);
      toast.success('Removed from dashboard successfully.');
    }

    if (isErrorDeleteHeapClusterFromDashboard) {
      toast.error('Failed to remove from dashboard. Please try again.');
    }
  }, [
    isSuccessDeleteHeapClusterFromDashboard,
    isErrorDeleteHeapClusterFromDashboard,
  ]);

  // Handlers.
  const onDeleteHeapSubmit = (
    heapCluster: HeapClusterResponseDataTypeObj | null,
  ) => {
    if (isNil(heapCluster)) {
      return;
    }

    // Delete all polygons of the heap.
    heapCluster.heaps.forEach((heap) => {
      polygonTool?.deleteById(heap.id);
    });

    // To handle the case when user selects a heap and deletes it.
    if (has(selectedClusters, heapCluster.id)) {
      onSelectClusterHandler?.(heapCluster);
    }

    sendDeleteHeapRequest({ id: heapCluster.id });
    setShowDeleteConfirmationCard(deleteHeapInitialState);
  };

  const jumpToHeap = (heap: HeapClusterResponseDataTypeObj) => {
    if (has(selectedClusters, heap.id) && cesiumProxy) {
      flyToCentroid(cesiumProxy.viewer, heap.centroid);
    }
  };

  const recalculateHeapVolume = (heap: HeapClusterResponseDataTypeObj) => {
    toast.info('Recalculating Layer Volume');
    sendRecalculateHeapVolumeRequest({ id: heap.id });
  };

  const onCloseEditHeap = () => {
    setShowEditHeapModal(false);
  };

  const handleOpenHeapTable = (cluster) => {
    if (activeHeapCluster?.clusterId === cluster.id) {
      setIsHeapsTableVisible(!isHeapsTableVisible);
    } else {
      setActiveHeapCluster({
        clusterName: cluster.name,
        clusterId: cluster.id,
      });
      setIsHeapsTableVisible(true);
    }
  };

  const onClickEditHeap = (heapId: string) => {
    setHeapId(heapId);
    setShowEditHeapModal(!showEditHeapModal);
  };

  const onClickDownloadHeap = (heapId: string) => {
    setHeapId(heapId);
    sendDownloadHeapRequest(heapId);
  };

  const onClickDownloadPdf = () => {
    if (selectedClusters) {
      setVolumeReportFormat(VolumeReportFormat.PDF);
      trackEvent(Events.ExportPdfClicked, {
        [EventPropertyKey.ExportFormat]: VolumeReportFormat.PDF,
      });
      sendDownloadVolumeReportRequest({
        heapIds: Object.keys(selectedClusters),
        responseFormat: VolumeReportFormat.PDF,
      });
    }
  };

  const onClickDownloadCsv = () => {
    if (selectedClusters) {
      setVolumeReportFormat(VolumeReportFormat.CSV);
      sendDownloadVolumeReportRequest({
        heapIds: Object.keys(selectedClusters),
        responseFormat: VolumeReportFormat.CSV,
      });
    }
  };

  const onClickBulkSelect = () => {
    if (bulkSelectCheckBoxStatus === HeapBulkSelectCheckBoxStatus.Checked) {
      // Remove selected heaps.
      heapListResponse?.data.forEach((heap) => {
        onSelectClusterHandler?.(heap);
      });
      setBulkSelectCheckBoxStatus(HeapBulkSelectCheckBoxStatus.Unchecked);
    } else if (
      bulkSelectCheckBoxStatus === HeapBulkSelectCheckBoxStatus.Unchecked
    ) {
      // Select all unselected heaps.
      heapListResponse?.data.forEach((heap) => {
        if (!has(selectedClusters, heap.id)) {
          onSelectClusterHandler?.(heap);
        }
      });
      setBulkSelectCheckBoxStatus(HeapBulkSelectCheckBoxStatus.Checked);
    } else {
      // Remove selected heaps.
      heapListResponse?.data.forEach((heap) => {
        if (has(selectedClusters, heap.id)) {
          onSelectClusterHandler?.(heap);
        }
      });
      setBulkSelectCheckBoxStatus(HeapBulkSelectCheckBoxStatus.Unchecked);
    }
  };

  const onClickSingleSelect = (
    event: React.MouseEvent<HTMLInputElement>,
    heap: HeapClusterResponseDataTypeObj,
    heapIndex: number,
  ) => {
    if (heap.status === ClusterStatus.Processing) {
      return;
    }

    event.stopPropagation();
    const { shiftKey } = event;

    /**
     * Shift + Select selects a range of heaps between start heap and end heap.
     * To select start heap user doesn't have to hold Shift key,
     * but to select end heap user has to press shift key.
     */
    if (
      shiftKey &&
      shiftKeyRangeSelectedHeap.startingIndex !== -1 &&
      shiftKeyRangeSelectedHeap.endingIndex === -1
    ) {
      setShiftKeyRangeSelectedHeap({
        ...shiftKeyRangeSelectedHeap,
        endingIndex: heapIndex,
      });

      const startIndex = Math.min(
        shiftKeyRangeSelectedHeap.startingIndex,
        heapIndex,
      );
      const endIndex = Math.max(
        shiftKeyRangeSelectedHeap.startingIndex,
        heapIndex,
      );

      heapListResponse?.data.forEach((heapItem, index) => {
        if (index >= startIndex && index <= endIndex) {
          // Start heap is selected, select heaps which are not selected till end heap.
          if (
            shiftKeyRangeSelectedHeap.isStartHeapSelected &&
            !has(selectedClusters, heapItem.id)
          ) {
            onSelectClusterHandler?.(heapItem);
          }

          // Start heap is not selected, unselect heaps till end heap.
          if (
            !shiftKeyRangeSelectedHeap.isStartHeapSelected &&
            has(selectedClusters, heapItem.id)
          ) {
            onSelectClusterHandler?.(heapItem);
          }
        }
      });
    } else {
      setShiftKeyRangeSelectedHeap({
        ...shiftKeyRangeSelectedHeap,
        startingIndex: heapIndex,
        endingIndex: -1,
        // changing the previous status of start heap after this thus the !
        isStartHeapSelected: !has(selectedClusters, heap.id),
      });
      onSelectClusterHandler?.(heap);
    }
  };

  const getValueClassName = (isAllFailed: boolean, isIncomplete: boolean) =>
    classNames({
      'heap-list__modal-label-value__error': isAllFailed,
      'heap-list__modal-label-value__warning': !isAllFailed && isIncomplete,
      'heap-list__modal-label-value': !isAllFailed && !isIncomplete,
    });

  const roundNumber = (value: string | null) => {
    if (!value) {
      return '-';
    }
    return Number.parseFloat(value).toFixed(2);
  };

  const handleCloseDashboardModal = () => {
    setIsDashboardModalVisible(false);
  };

  const handleOpenDashboardModal = async (
    cluster: HeapClusterResponseDataTypeObj,
  ) => {
    if (cluster.status === ClusterStatus.Failed) {
      return;
    }

    try {
      await refetchDashboardConfig();

      setSelectedDashboardCluster(cluster);
      setIsDashboardModalVisible(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleShowRemoveDashboardRecordModal = (
    cluster: HeapClusterResponseDataTypeObj,
  ) => {
    setSelectedDashboardCluster(cluster);
    setIsRemoveDashboardRecordModalVisible(true);
  };

  const handleCloseRemoveDashboardRecordModal = () => {
    setIsRemoveDashboardRecordModalVisible(false);
  };

  const handleConfirmRemoveDashboardRecord = () => {
    if (isNil(selectedDashboardCluster?.id)) {
      return;
    }

    deleteHeapClusterFromDashboard(selectedDashboardCluster?.id);
  };

  useEffect(() => {
    if (isNil(scrollToClusterId)) {
      return;
    }

    const element = document.getElementById(scrollToClusterId);

    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setScrollToClusterId?.(null);
    }
  }, [scrollToClusterId, isHeapListLoading, heapListResponse?.data]);

  // Renders.
  return (
    <div className="heap-list-content-container">
      {isHeapListLoading ? (
        <Spinner />
      ) : size(heapListResponse?.data) === 0 ? (
        <div className="heap-list--empty">No heaps present.</div>
      ) : (
        <>
          <div className="heap-list">
            <div className="heap-list__header">
              <span>Heap Clusters</span>
              <Input.CheckBox
                onClick={() => {
                  onClickBulkSelect();
                }}
                indeterminate={
                  bulkSelectCheckBoxStatus ===
                  HeapBulkSelectCheckBoxStatus.Indeterminate
                }
                checked={
                  bulkSelectCheckBoxStatus ===
                  HeapBulkSelectCheckBoxStatus.Checked
                }
              />
            </div>
            <Accordion
              // To access the currently opened accordion
              onSelect={(activeIndex) => {
                setActiveKey(activeIndex);
              }}
              variant={AccordionVariant.ChevronRight}
              className="heap-list-accordion"
            >
              {heapListResponse?.data.map(
                (cluster: HeapClusterResponseDataTypeObj, index: number) => {
                  const isProcessing =
                    cluster.status === ClusterStatus.Processing;
                  const isFailed = cluster.status === ClusterStatus.Failed;
                  const isAllFailed =
                    isFailed && cluster.numberOfCalculatedHeaps === 0;
                  const isIncomplete =
                    isFailed && cluster.numberOfCalculatedHeaps > 0;

                  const checkboxClassName = classNames(
                    'heap-list__modal-checkbox',
                    {
                      'heap-list__modal-checkbox__hidden': isProcessing,
                    },
                  );

                  const calculationValuesClassName = getValueClassName(
                    isAllFailed,
                    isIncomplete,
                  );

                  const isClusterSelected = has(selectedClusters, cluster.id);

                  return (
                    <div key={cluster.id} id={cluster.id}>
                      <div className="heap-list-container">
                        <CheckBox
                          alignCheckbox={CheckboxAlignment.Right}
                          showCard
                          checked={has(selectedClusters, cluster.id)}
                          onClick={(event) => {
                            onClickSingleSelect(event, cluster, index);
                          }}
                          onChange={() => {}}
                          className={checkboxClassName}
                        >
                          {cluster.name}
                        </CheckBox>
                        <div className="heap-list__modal-body">
                          <div>
                            <div className="heap-list__modal-text">
                              <div className="heap-list__modal-detail">
                                <div className="heap-list__modal-detail-header">
                                  <span className="heap-list__modal-detail-header__text">
                                    No. of Heaps
                                  </span>
                                </div>
                                <span>{cluster.numberOfHeaps || '-'}</span>
                              </div>
                              <div className="heap-list__modal-detail">
                                <div className="heap-list__modal-detail-header">
                                  <span className="heap-list__modal-detail-header__text">
                                    Material
                                  </span>
                                </div>
                                {getMaterialTypeLabel(cluster.materialType)}
                              </div>
                              <div className="heap-list__modal-detail">
                                <div className="heap-list__modal-detail-header">
                                  <span className="heap-list__modal-detail-header__text">
                                    Bulk Density
                                  </span>
                                </div>
                                {isNil(cluster.bulkDensity) ? (
                                  <span>-</span>
                                ) : (
                                  <>
                                    <span>{`${cluster.bulkDensity} `}</span>
                                    <span className="heap-list__modal-detail-header__unit">
                                      (gm/cm<sup>3</sup>)
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="heap-list__modal-sub-header">
                              <div className="heap-list__modal-sub-header__text">
                                Cumulative Volume{' '}
                                <span className="heap-list__modal-sub-header__text__unit">
                                  (m<sup>3</sup>)
                                </span>
                              </div>
                              <div className="heap-list__modal-sub-header__divider" />
                              <HeapClusterCalculationStatus
                                status={cluster.status}
                                numberOfCalculatedHeaps={
                                  cluster.numberOfCalculatedHeaps
                                }
                                numberOfHeaps={cluster.numberOfHeaps}
                                failureReason={cluster.failureReason}
                              />
                            </div>
                            {isProcessing ? (
                              <div className="heap-list__modal-loading">
                                <div className="heap-list__modal-loading__text">
                                  {`${cluster.numberOfCalculatedHeaps}/${cluster.numberOfHeaps} calculated`}
                                </div>
                                <div className="heap-list__modal-loading__spinner">
                                  <Spinner className="heap-list__modal-loading__spinner-icon" />
                                </div>
                              </div>
                            ) : (
                              <div className="heap-list__modal-text">
                                <div className="heap-list__modal-detail">
                                  <span className="heap-list__modal-label">
                                    Cut
                                  </span>
                                  <span className={calculationValuesClassName}>
                                    {roundNumber(cluster.cumulativeCutVolume)}
                                  </span>
                                </div>
                                <div className="heap-list__modal-detail">
                                  <span className="heap-list__modal-label">
                                    Fill
                                  </span>
                                  <span className={calculationValuesClassName}>
                                    {roundNumber(cluster.cumulativeFillVolume)}
                                  </span>
                                </div>
                                <div className="heap-list__modal-detail">
                                  <span className="heap-list__modal-label">
                                    Net
                                  </span>
                                  <span className={calculationValuesClassName}>
                                    {roundNumber(cluster.cumulativeNetVolume)}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="heap-list__modal-sub-header">
                              <div className="heap-list__modal-sub-header__text">
                                Cumulative Weight{' '}
                                <span className="heap-list__modal-sub-header__text__unit">
                                  (tonnes)
                                </span>
                              </div>
                              <div className="heap-list__modal-sub-header__divider" />
                              <HeapClusterCalculationStatus
                                status={cluster.status}
                                numberOfCalculatedHeaps={
                                  cluster.numberOfCalculatedHeaps
                                }
                                numberOfHeaps={cluster.numberOfHeaps}
                                failureReason={cluster.failureReason}
                              />
                            </div>
                            {isProcessing ? (
                              <div className="heap-list__modal-loading">
                                <div className="heap-list__modal-loading__text">
                                  {`${cluster.numberOfCalculatedHeaps}/${cluster.numberOfHeaps} calculated`}
                                </div>
                                <div className="heap-list__modal-loading__spinner">
                                  <Spinner className="heap-list__modal-loading__spinner-icon" />
                                </div>
                              </div>
                            ) : (
                              <div className="heap-list__modal-text">
                                <div className="heap-list__modal-detail">
                                  <span className="heap-list__modal-label">
                                    Cut
                                  </span>
                                  <span className={calculationValuesClassName}>
                                    {roundNumber(cluster.cumulativeCutWeight)}
                                  </span>
                                </div>
                                <div className="heap-list__modal-detail">
                                  <span className="heap-list__modal-label">
                                    Fill
                                  </span>
                                  <span className={calculationValuesClassName}>
                                    {roundNumber(cluster.cumulativeFillWeight)}
                                  </span>
                                </div>
                                <div className="heap-list__modal-detail">
                                  <span className="heap-list__modal-label">
                                    Net
                                  </span>
                                  <span className={calculationValuesClassName}>
                                    {roundNumber(cluster.cumulativeNetWeight)}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                          {!cluster.numberOfHeaps && (
                            <FormMessage
                              message="This layer has multiple polygons. Recalculate to view individual volumes."
                              variant={FormMessageVariant.Info}
                              className="heap-list__modal-body-info-message"
                              showIcon
                            />
                          )}
                        </div>
                        <div className="heap-list__btn-container">
                          <Tooltip hoverText="Delete">
                            <Icon
                              className="heap-list__btn heap-list__btn-delete"
                              identifier={IconIdentifier.Bin}
                              isDisabled={isProcessing}
                              onClick={() =>
                                setShowDeleteConfirmationCard({
                                  show: true,
                                  cluster,
                                })
                              }
                            />
                          </Tooltip>
                          <Tooltip hoverText="Edit">
                            <Icon
                              className="heap-list__btn heap-list__btn-edit"
                              identifier={IconIdentifier.Pencil}
                              onClick={() => onClickEditHeap(cluster.id)}
                            />
                          </Tooltip>
                          <Tooltip hoverText="Download .geojson">
                            <Icon
                              className="heap-list__btn heap-list__btn-download"
                              onClick={() => onClickDownloadHeap(cluster.id)}
                              identifier={IconIdentifier.Download}
                            />
                          </Tooltip>
                          <Tooltip hoverText="Recalculate Volume">
                            <Icon
                              className="heap-list__btn heap-list__btn-redo"
                              identifier={IconIdentifier.Reset}
                              isDisabled={isProcessing}
                              onClick={() => recalculateHeapVolume(cluster)}
                            />
                          </Tooltip>
                          <Tooltip
                            hoverText={
                              !cluster.numberOfHeaps
                                ? 'Recalculate volume to view table'
                                : 'Heap Table'
                            }
                          >
                            <Icon
                              className="heap-list__btn heap-list__btn-row"
                              identifier={IconIdentifier.TableRow}
                              isDisabled={
                                isProcessing ||
                                !cluster.numberOfHeaps ||
                                !isClusterSelected
                              }
                              onClick={() => handleOpenHeapTable(cluster)}
                            />
                          </Tooltip>
                          <Tooltip hoverText="Zoom to Layer">
                            <Icon
                              className="heap-list__btn heap-list__btn-jumpto"
                              identifier={IconIdentifier.GCPAvailableTagged}
                              onClick={() => jumpToHeap(cluster)}
                              isDisabled={isProcessing || !isClusterSelected}
                            />
                          </Tooltip>
                        </div>

                        {/* Dashboard Actions */}
                        {isProductivityDashboardEnabled && (
                          <div className="heap-list__dashboard">
                            {isFailed ? (
                              <>
                                <div className="heap-list__modal-sub-header">
                                  <div className="heap-list__modal-sub-header__text">
                                    Dashboard Actions
                                  </div>
                                  <div className="heap-list__modal-sub-header__divider" />
                                  <div className="heap-list__modal-sub-header__heap-count">
                                    Unavailable
                                  </div>
                                </div>
                                <div className="heap-list__dashboard-unavailable">
                                  <Icon
                                    identifier={
                                      IconIdentifier.ExclamationCircle
                                    }
                                    size={14}
                                    colorClass={ColorClass.Neutral250}
                                  />
                                  <span className="heap-list__dashboard-unavailable__text">
                                    Dashboard actions unavailable for failed
                                    calculations.
                                  </span>
                                </div>
                              </>
                            ) : (
                              <>
                                <Divider text="Dashboard Actions" />

                                <div className="heap-list__dashboard-actions">
                                  {cluster.includeInDashboard ? (
                                    <>
                                      <Button
                                        variant={ButtonVariant.Outline}
                                        leftIconIdentifier={
                                          IconIdentifier.Pencil
                                        }
                                        disabled={!isClusterSelected}
                                        onClick={() =>
                                          handleOpenDashboardModal(cluster)
                                        }
                                        isLoading={isDashboardConfigLoading}
                                      >
                                        Edit
                                      </Button>

                                      <Button
                                        variant={ButtonVariant.DangerOutline}
                                        leftIconIdentifier={IconIdentifier.Bin}
                                        disabled={!isClusterSelected}
                                        onClick={() =>
                                          handleShowRemoveDashboardRecordModal(
                                            cluster,
                                          )
                                        }
                                      >
                                        Remove
                                      </Button>
                                    </>
                                  ) : (
                                    <Button
                                      variant={ButtonVariant.Outline}
                                      leftIconIdentifier={
                                        IconIdentifier.Dashboard
                                      }
                                      disabled={!isClusterSelected}
                                      onClick={() =>
                                        handleOpenDashboardModal(cluster)
                                      }
                                      isLoading={isDashboardConfigLoading}
                                    >
                                      Add to dashboard
                                    </Button>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        )}

                        <Accordion.Item
                          title={
                            activeKey === `heap-accordion-${index}`
                              ? 'Less Details'
                              : 'More Details'
                          }
                          eventKey={`heap-accordion-${index}`}
                          iconIdentifier={IconIdentifier.ArrowDown}
                          iconSize={16}
                        >
                          <div className="heap-list__modal-body--extended">
                            <div className="heap-list__modal-detail-container">
                              <div className="heap-list__modal-sub-header">
                                <div className="heap-list__modal-sub-header__text">
                                  Base Reference
                                </div>
                                <div className="heap-list__modal-sub-header__divider" />
                              </div>
                              <div className="heap-list__modal-text base-reference">
                                <div className="heap-list__modal-detail">
                                  <span className="heap-list__modal-label">
                                    {baseReferenceLabel[cluster.baseReference]}
                                  </span>
                                  {cluster.baseReference ===
                                    BaseReference.OtherIterationDsm && (
                                    <span className="other-iteration-name">
                                      {cluster.baseIteration}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="heap-list__modal-detail-container">
                              <div className="heap-list__modal-sub-header">
                                <div className="heap-list__modal-sub-header__text">
                                  Category
                                </div>
                                <div className="heap-list__modal-sub-header__divider" />
                              </div>

                              <div className="heap-list__modal-detail ">
                                <div className="category">
                                  {Formatter.toTitleCase(cluster.category, '_')}
                                </div>
                              </div>
                            </div>

                            <div>
                              <div className="heap-list__modal-sub-header">
                                <div className="heap-list__modal-sub-header__text">
                                  Description
                                </div>
                                <div className="heap-list__modal-sub-header__divider" />
                              </div>
                              <div className="heap-list__modal-text base-reference">
                                <div className="heap-list__modal-detail">
                                  {cluster.remarks}
                                </div>
                              </div>
                            </div>
                          </div>
                        </Accordion.Item>
                      </div>
                    </div>
                  );
                },
              )}
              <EditHeap
                onClose={onCloseEditHeap}
                heapId={heapId}
                refetchHeapList={heapListRefetch}
                show={showEditHeapModal}
                setIsHeapClusterModified={setIsHeapClusterModified}
              />
            </Accordion>
          </div>
          <div className="heap-list__footer">
            <Button
              onClick={onClickDownloadCsv}
              disabled={isEmpty(selectedClusters)}
              isLoading={
                isVolumeReportLoading &&
                volumeReportFormat == VolumeReportFormat.CSV
              }
              variant={ButtonVariant.Secondary}
            >
              Download CSV
            </Button>
            <Button
              onClick={onClickDownloadPdf}
              disabled={isEmpty(selectedClusters)}
              isLoading={
                isVolumeReportLoading &&
                volumeReportFormat == VolumeReportFormat.PDF
              }
            >
              Export PDF
            </Button>
          </div>

          {showDeleteConfirmationCard.show &&
            !isNil(showDeleteConfirmationCard.cluster) && (
              <ConfirmationModal
                isConfirmDanger
                title="Delete Confirmation"
                message={
                  <>
                    Are you sure you want to delete{' '}
                    <b>{showDeleteConfirmationCard.cluster.name}</b>?
                  </>
                }
                confirmText="Delete"
                onSubmit={() =>
                  onDeleteHeapSubmit(showDeleteConfirmationCard.cluster)
                }
                onClose={() =>
                  setShowDeleteConfirmationCard(deleteHeapInitialState)
                }
              />
            )}

          {/* Dashboard Modal */}
          {!isNil(selectedDashboardCluster) && isDashboardModalVisible && (
            <DashboardModal
              onClose={handleCloseDashboardModal}
              selectedDashboardCluster={selectedDashboardCluster}
              dashboardCategoryConfig={dashboardCategoryConfig}
              refetchHeapList={heapListRefetch}
            />
          )}

          {/* Remove Dashboard Record Modal */}
          {isRemoveDashboardRecordModalVisible &&
            !isNil(selectedDashboardCluster) && (
              <ConfirmationModal
                title="Remove from dashboard"
                message={
                  <>
                    Are you sure you want to remove{' '}
                    <b>{selectedDashboardCluster.name}</b> record from the
                    dashboard?
                    <FormMessage
                      variant={FormMessageVariant.Warning}
                      message="Dashboard values and KPIs will update once removed."
                    />
                  </>
                }
                confirmText="Yes, remove"
                isConfirmDanger
                onClose={handleCloseRemoveDashboardRecordModal}
                onSubmit={handleConfirmRemoveDashboardRecord}
                isConfirmButtonLoading={isDeleteHeapClusterFromDashboardLoading}
              />
            )}
        </>
      )}
    </div>
  );
};
